import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import toast from 'react-hot-toast';
import { useFileSearch, useFileJourney } from '../../hooks';
import { partnersApi, tasksApi } from '../../services/api';
import { Partner } from '../../types/partner';
import { Task } from '../../types/task';
import { FileEventType, NotificationBlock } from '../../types/fileAudit';
import { Card, CardHeader, CardBody } from '../shared/Card';
import { Button } from '../shared/Button';
import { LoadingSkeleton } from '../shared/LoadingSkeleton';
import { StatusBadge } from '../shared/StatusBadge';
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';

// Stable empty references: `?? []` would be a new array on every render.
const NO_PARTNERS: Partner[] = [];
const NO_TASKS: Task[] = [];
// Dropdown option lists tolerate a short stale window; see the note at the queries below.
const OPTIONS_STALE_TIME = 30_000;

// THUB-197: download icon for the archive file download action
const DownloadIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

/**
 * FileAuditPage component
 *
 * Main page for file audit functionality that integrates search and journey views.
 * Provides search filtering by filename, partner, task, and date range with
 * journey detail viewing via a slide-out panel.
 */
export const FileAuditPage: React.FC = () => {
  // Filter dropdown state

  // Search form state
  const [filename, setFilename] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Selected file for journey view
  const [selectedFile, setSelectedFile] = useState<{
    fileId: string;
    filename: string;
  } | null>(null);

  // File search hook
  const {
    results,
    isLoading: searchLoading,
    error: searchError,
    search,
    updateQuery,
    resetQuery,
    loadMore,
    hasMore,
  } = useFileSearch({
    onSearchComplete: () => setHasSearched(true),
    onSearchError: () => setHasSearched(true),
  });

  // File journey hook - only fetch when a file is selected
  const {
    journey,
    isLoading: journeyLoading,
    error: journeyError,
    refresh: refreshJourney,
  } = useFileJourney(selectedFile?.fileId ?? null);

  // THUB-299: was two useState lists plus loadingOptions/optionsError flags filled by a mount
  // effect, so every visit to File Audit refetched both lists -- even though PartnerList and
  // TaskList have already fetched exactly these two reads. Sharing their query keys means an
  // already-populated cache serves the dropdowns.
  //
  // staleTime is what makes that true rather than merely relocating the request: with TanStack's
  // default of 0 the cached options render instantly but a background refetch still fires. A
  // stale window: for PARTNERS, freshness still comes from INVALIDATION -- partner_status_changes
  // invalidates partners.all (lib/wsInvalidationMap.ts, coalesced by App.tsx) and
  // invalidateQueries marks data stale regardless of staleTime, so a renamed or deleted partner
  // reaches that dropdown at once. That topic is low-volume, so the reach is free.
  //
  // For TASKS it deliberately does NOT: execution_updates is one broadcast per file and maps to
  // tasks.all, which made this dropdown re-read a ~423 KB list ~2.09x/second during a batch window
  // for information an execution event cannot change. The task list is therefore keyed on
  // queryKeys.taskOptions, outside that prefix, and refreshed by staleTime on revisit instead.
  const partnersQuery = useQuery({
    queryKey: queryKeys.partners.all,
    queryFn: () => partnersApi.getPartners(),
    staleTime: OPTIONS_STALE_TIME,
  });
  // Keyed on taskOptions, NOT tasks.all -- see queryKeys.taskOptions. Under tasks.all this
  // dropdown refetched a ~423 KB list ~2.09x/second during a batch window, and an execution event
  // cannot change a task's name or partner, so none of it could alter what renders here.
  const tasksQuery = useQuery({
    queryKey: queryKeys.taskOptions,
    queryFn: () => tasksApi.getTasks(),
    staleTime: OPTIONS_STALE_TIME,
  });

  const partners: Partner[] = partnersQuery.data ?? NO_PARTNERS;
  const tasks: Task[] = tasksQuery.data ?? NO_TASKS;
  const loadingOptions = partnersQuery.isPending || tasksQuery.isPending;
  // One shared message, as before: the page stays usable on either list failing.
  const optionsError =
    partnersQuery.error || tasksQuery.error ? 'Failed to load filter options' : null;


  // Filter tasks by selected partner and exclude tasks with missing names
  const filteredTasks = (partnerId
    ? tasks.filter((task) => task.partnerId === partnerId)
    : tasks
  ).filter((task) => task.name != null && task.name.trim() !== '');

  // Handle search form submission
  const handleSearch = useCallback(() => {
    // THUB-130: build the filter object ONCE and pass it to both updateQuery
    // (for display/state) and search (for the request). Previously this did
    // updateQuery(...) then setTimeout(() => search()), but `search` closed over
    // the pre-update query, so the first search after selecting a filter fired
    // with empty params and the backend returned arbitrary scan rows. Passing
    // the fresh object into search() removes the stale-closure race.
    const queryUpdates = {
      filename: filename || undefined,
      partnerId: partnerId || undefined,
      taskId: taskId || undefined,
      dateRange:
        startDate || endDate
          ? {
              start: startDate || new Date(0).toISOString().split('T')[0],
              end: endDate || new Date().toISOString().split('T')[0],
            }
          : undefined,
    };
    updateQuery(queryUpdates);
    search(queryUpdates);
  }, [filename, partnerId, taskId, startDate, endDate, updateQuery, search]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setFilename('');
    setPartnerId('');
    setTaskId('');
    setStartDate('');
    setEndDate('');
    setHasSearched(false);
    resetQuery();
  }, [resetQuery]);

  // Handle partner change - clear task if it doesn't belong to new partner
  const handlePartnerChange = useCallback(
    (newPartnerId: string) => {
      setPartnerId(newPartnerId);
      if (taskId) {
        const selectedTask = tasks.find((t) => t.taskId === taskId);
        if (selectedTask && selectedTask.partnerId !== newPartnerId) {
          setTaskId('');
        }
      }
    },
    [taskId, tasks]
  );

  // Handle viewing file journey
  const handleViewJourney = useCallback(
    (fileId: string, fileFilename: string) => {
      setSelectedFile({ fileId, filename: fileFilename });
    },
    []
  );

  // Handle closing journey panel
  const handleCloseJourney = useCallback(() => {
    setSelectedFile(null);
  }, []);

  // THUB-197: track which file key is currently downloading (for spinner/disable)
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  // THUB-197: request a pre-signed URL for an archived file and hand it to the
  // browser. Requires both partnerId and the bare S3 key; server-side scoping
  // already restricts access, so we only render the action when both exist.
  const handleDownload = useCallback(
    async (partnerId: string | undefined, fileKey: string | undefined, filename: string) => {
      if (!partnerId || !fileKey) {
        toast.error('This file cannot be downloaded (missing archive location).');
        return;
      }
      setDownloadingKey(fileKey);
      try {
        const { downloadUrl, filename: serverFilename } = await partnersApi.downloadPartnerFile(
          partnerId,
          fileKey
        );
        if (!downloadUrl) {
          throw new Error('No download URL returned');
        }
        // Trigger the download via a transient anchor pointing at the presigned URL.
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = serverFilename || filename || '';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Downloading ${serverFilename || filename}`);
      } catch (err: any) {
        console.error('File download failed:', err);
        const message =
          err?.statusCode === 403 || err?.code === 'FORBIDDEN'
            ? 'You are not authorized to download this file.'
            : err?.statusCode === 404 || err?.code === 'NOT_FOUND'
            ? 'File not found in the archive.'
            : 'Failed to download file. Please try again.';
        toast.error(message);
      } finally {
        setDownloadingKey(null);
      }
    },
    []
  );

  // Handle keyboard submit
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  // Check if any filters are applied
  const hasFilters = filename || partnerId || taskId || startDate || endDate;

  // THUB-184: present Transfer History newest-first. The backend returns rows in
  // DynamoDB scan/hash order on the unfiltered scan branch (no server-side sort),
  // so without this the most recent transfer can land anywhere in the list.
  // Sorting the already-fetched items here does NOT affect cursor pagination
  // (Load More still uses nextCursor); it just orders the accumulated window.
  const sortedItems = useMemo(() => {
    const items = results?.items;
    if (!items) return items;
    return [...items].sort((a, b) => {
      const ta = new Date(a.timestamp).getTime();
      const tb = new Date(b.timestamp).getTime();
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    });
  }, [results]);

  // Export results to CSV
  const handleExportResults = useCallback(() => {
    if (!sortedItems || sortedItems.length === 0) return;

    const headers = [
      'Timestamp',
      'Filename',
      'Partner',
      'Task',
      'Event Type',
      'Status',
      'File Size',
      'Checksum',
      'Error',
    ];
    const rows = sortedItems.map((event) => [
      event.timestamp,
      event.filename,
      event.partnerName || event.partnerId,
      event.taskName || event.taskId,
      event.eventType || '',
      event.status,
      event.fileSize?.toString() || event.sizeBytes?.toString() || '',
      event.checksum || '',
      event.errorMessage || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `file-audit-export-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [sortedItems]);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-slate-900">
      {/* Page Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-neutral-200 dark:border-slate-700">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-display font-bold text-neutral-900 dark:text-white">
                Transfer History
              </h1>
              <p className="mt-1 text-sm text-neutral-500 dark:text-slate-400">
                Search and track file transfers across the system
              </p>
            </div>
            {results && results.items.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportResults}
                leftIcon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                }
              >
                Export Results
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Form */}
        <Card className="mb-6">
          <CardHeader title="Search Filters" subtitle="Filter file events by various criteria" />
          <CardBody>
            {optionsError && (
              <div className="mb-4 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                <p className="text-sm text-danger-700 dark:text-danger-400">{optionsError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Filename */}
              <div>
                <label
                  htmlFor="filename"
                  className="block text-sm font-medium text-neutral-700 dark:text-slate-300 mb-1"
                >
                  Filename
                </label>
                <input
                  id="filename"
                  type="text"
                  placeholder="Search by filename..."
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={searchLoading}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-neutral-300 dark:border-slate-600 rounded-lg text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* Partner Select */}
              <div>
                <label
                  htmlFor="partner"
                  className="block text-sm font-medium text-neutral-700 dark:text-slate-300 mb-1"
                >
                  Partner
                </label>
                <select
                  id="partner"
                  value={partnerId}
                  onChange={(e) => handlePartnerChange(e.target.value)}
                  disabled={searchLoading || loadingOptions}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-neutral-300 dark:border-slate-600 rounded-lg text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">All Partners</option>
                  {partners.map((partner) => (
                    <option key={partner.partnerId} value={partner.partnerId}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Task Select */}
              <div>
                <label
                  htmlFor="task"
                  className="block text-sm font-medium text-neutral-700 dark:text-slate-300 mb-1"
                >
                  Task
                </label>
                <select
                  id="task"
                  value={taskId}
                  onChange={(e) => setTaskId(e.target.value)}
                  disabled={searchLoading || loadingOptions}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-neutral-300 dark:border-slate-600 rounded-lg text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">All Tasks</option>
                  {filteredTasks.map((task) => (
                    <option key={task.taskId} value={task.taskId}>
                      {task.name}
                      {task.partnerName ? ` (${task.partnerName})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label
                  htmlFor="startDate"
                  className="block text-sm font-medium text-neutral-700 dark:text-slate-300 mb-1"
                >
                  Start Date
                </label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={searchLoading}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-neutral-300 dark:border-slate-600 rounded-lg text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* End Date */}
              <div>
                <label
                  htmlFor="endDate"
                  className="block text-sm font-medium text-neutral-700 dark:text-slate-300 mb-1"
                >
                  End Date
                </label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={searchLoading}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-neutral-300 dark:border-slate-600 rounded-lg text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              {hasFilters && (
                <Button
                  variant="ghost"
                  onClick={handleClearFilters}
                  disabled={searchLoading}
                >
                  Clear Filters
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleSearch}
                isLoading={searchLoading}
                leftIcon={
                  !searchLoading ? (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  ) : undefined
                }
              >
                Search
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Search Error */}
        {searchError && (
          <div className="mb-6 p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg
                className="w-5 h-5 text-danger-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm text-danger-700 dark:text-danger-400">
                {searchError}
              </span>
            </div>
            <button
              onClick={() => handleSearch()}
              className="text-sm text-danger-600 dark:text-danger-400 hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Search Results */}
        <Card>
          <CardBody padding="none">
            {/* Loading State */}
            {searchLoading && !results && (
              <div className="p-6">
                <LoadingSkeleton type="table" rows={5} />
              </div>
            )}

            {/* Empty State - Before Search */}
            {!hasSearched && !searchLoading && (
              <div className="py-16 px-6 text-center">
                <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-slate-800 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-neutral-400 dark:text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                  Search File Events
                </h3>
                <p className="text-neutral-500 dark:text-slate-400 max-w-sm mx-auto">
                  Use the filters above to search for file events and track their journey through the system.
                </p>
              </div>
            )}

            {/* Empty State - No Results */}
            {hasSearched && !searchLoading && results && results.items.length === 0 && (
              <div className="py-16 px-6 text-center">
                <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-slate-800 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-neutral-400 dark:text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                  No File Events Found
                </h3>
                <p className="text-neutral-500 dark:text-slate-400 max-w-sm mx-auto">
                  Try adjusting your search filters or date range.
                </p>
              </div>
            )}

            {/* Results Table */}
            {results && results.items.length > 0 && (
              <div className="overflow-x-auto">
                {/* Results Header */}
                <div className="px-6 py-4 border-b border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-900/50">
                  {/* THUB-244: rows are FILES now, not events. The total is deliberately
                      omitted: DynamoDB cannot count distinct files without reading everything, so
                      any figure here would be an event count masquerading as a file count -- and a
                      wrong number is worse than none. "Load More" still reveals the rest. */}
                  <span className="text-sm text-neutral-600 dark:text-slate-400">
                    {results.items.length === 1 ? '1 file' : `${results.items.length} files`}
                  </span>
                </div>

                {/* Table */}
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-900/50">
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                        Filename
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                        Partner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                        Task
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 dark:divide-slate-700">
                    {(sortedItems ?? results.items).map((event) => (
                      <tr
                        key={event.eventId || event.id || `${event.fileId}-${event.timestamp}`}
                        className="hover:bg-neutral-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                        onClick={() => handleViewJourney(event.fileId, event.filename)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white">
                          {formatTimestamp(event.timestamp)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-neutral-900 dark:text-white truncate max-w-[200px]">
                            {event.filename}
                          </div>
                          {(event.fileSize || event.sizeBytes) && (
                            <div className="text-xs text-neutral-500 dark:text-slate-400">
                              {formatFileSize(event.fileSize || event.sizeBytes)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600 dark:text-slate-400">
                          {event.partnerName || event.partnerId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600 dark:text-slate-400">
                          {event.taskName || event.taskId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge
                            variant={getStatusVariant(event.status)}
                            size="sm"
                          >
                            {event.status}
                          </StatusBadge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* THUB-197: download the archived file. Only shown
                                when the row carries a partnerId AND an actual
                                archived key — i.e. s3Key under the archive/
                                prefix. Pre-archive events (FILE_LANDED /
                                FILE_PROCESSING / FILE_VALIDATED) carry the
                                staging/landing key, which has no downloadable
                                archived object yet and 404s, so no button there. */}
                            {event.partnerId && event.s3Key && event.s3Key.startsWith('archive/') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                isLoading={downloadingKey === event.s3Key}
                                title="Download archived file"
                                aria-label={`Download ${event.filename}`}
                                leftIcon={
                                  downloadingKey === event.s3Key ? undefined : (
                                    <DownloadIcon />
                                  )
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(event.partnerId, event.s3Key, event.filename);
                                }}
                              >
                                Download
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewJourney(event.fileId, event.filename);
                              }}
                            >
                              View Journey
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Load More */}
                {hasMore && (
                  <div className="px-6 py-4 border-t border-neutral-200 dark:border-slate-700 flex justify-center">
                    <Button
                      variant="secondary"
                      onClick={loadMore}
                      isLoading={searchLoading}
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Journey Detail Slide-out Panel */}
      {selectedFile && (
        <div
          className="fixed inset-0 z-50 overflow-hidden"
          aria-labelledby="slide-over-title"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            aria-hidden="true"
            onClick={handleCloseJourney}
          />

          {/* Panel */}
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <div className="pointer-events-auto w-screen max-w-lg">
              <div className="flex h-full flex-col bg-white dark:bg-slate-800 shadow-xl">
                {/* Panel Header */}
                <div className="px-6 py-4 border-b border-neutral-200 dark:border-slate-700">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2
                        id="slide-over-title"
                        className="text-lg font-display font-semibold text-neutral-900 dark:text-white"
                      >
                        File Journey
                      </h2>
                      <p className="mt-1 text-sm text-neutral-500 dark:text-slate-400 truncate max-w-xs">
                        {selectedFile.filename}
                      </p>
                      <p className="mt-0.5 text-xs font-mono text-neutral-400 dark:text-slate-500 truncate max-w-xs">
                        ID: {selectedFile.fileId}
                      </p>
                      {/* THUB-197: download the archived file from the journey
                          panel. Enabled once the journey has loaded an archive
                          location (partnerId + archivePath). */}
                      {journey?.partnerId && journey?.archivePath && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="mt-3"
                          isLoading={downloadingKey === journey.archivePath}
                          leftIcon={
                            downloadingKey === journey.archivePath ? undefined : <DownloadIcon />
                          }
                          onClick={() =>
                            handleDownload(
                              journey.partnerId,
                              journey.archivePath,
                              journey.filename || selectedFile.filename
                            )
                          }
                        >
                          Download File
                        </Button>
                      )}
                    </div>
                    <button
                      type="button"
                      className="p-2 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
                      onClick={handleCloseJourney}
                      aria-label="Close panel"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Panel Content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {/* Loading State */}
                  {journeyLoading && (
                    <div className="space-y-4">
                      <LoadingSkeleton type="text" lines={2} />
                      <LoadingSkeleton type="card" height="6rem" />
                      <LoadingSkeleton type="card" height="6rem" />
                      <LoadingSkeleton type="card" height="6rem" />
                    </div>
                  )}

                  {/* Error State */}
                  {journeyError && !journeyLoading && (
                    <div className="text-center py-8">
                      <div className="mx-auto w-12 h-12 mb-4 rounded-full bg-danger-100 dark:bg-danger-900/20 flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-danger-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-danger-700 dark:text-danger-400 mb-4">
                        {journeyError}
                      </p>
                      <Button variant="secondary" size="sm" onClick={refreshJourney}>
                        Retry
                      </Button>
                    </div>
                  )}

                  {/* Journey Timeline */}
                  {journey && !journeyLoading && !journeyError && (
                    <JourneyTimeline journey={journey} />
                  )}

                  {/* Empty State */}
                  {!journey && !journeyLoading && !journeyError && (
                    <div className="text-center py-8">
                      <p className="text-sm text-neutral-500 dark:text-slate-400">
                        No journey data available for this file.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Journey Timeline component for displaying file events
 */
interface JourneyTimelineProps {
  journey: {
    fileId: string;
    filename: string;
    events?: Array<{
      eventId?: string;
      id?: string;
      eventType?: FileEventType;
      status: string;
      timestamp: string;
      taskName?: string;
      partnerName?: string;
      sourcePath?: string;
      destinationPath?: string;
      fileSize?: number;
      checksum?: string;
      errorMessage?: string;
      metadata?: Record<string, unknown>;
      // THUB-205: exact published SNS notification (null when none/failed publish)
      notification?: NotificationBlock | null;
    }>;
  };
}

const JourneyTimeline: React.FC<JourneyTimelineProps> = ({ journey }) => {
  // THUB-298: the default expansion is DERIVED from the journey; only the operator's own
  // toggles are state.
  //
  // It used to be a Set in useState that an effect overwrote whenever journey.fileId changed --
  // with an exhaustive-deps suppression, because listing the real dependency (sortedEvents) would
  // have re-collapsed the panel on every refetch. Deriving the default removes the need for that
  // suppression: `expandedEvents` falls back to first-and-last until the operator touches
  // something, and a background refetch can no longer discard what they opened.
  const [expandedOverride, setExpandedOverride] = useState<Set<string> | null>(null);

  // Sort events by timestamp (oldest first for timeline). Memoized because it built a NEW array
  // on every render, which made it useless as a dependency for anything downstream.
  const sortedEvents = useMemo(
    () =>
      journey.events
        ? [...journey.events].sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          )
        : [],
    [journey.events]
  );

  // First and last are the interesting ones: where the file entered and where it ended up.
  const defaultExpanded = useMemo(() => {
    const keys = new Set<string>();
    if (sortedEvents.length > 0) keys.add(getEventKey(sortedEvents[0]));
    if (sortedEvents.length > 1) keys.add(getEventKey(sortedEvents[sortedEvents.length - 1]));
    return keys;
  }, [sortedEvents]);

  const expandedEvents = expandedOverride ?? defaultExpanded;

  /** Same signature the toggle/expandAll/collapseAll handlers already use. */
  const setExpandedEvents = (
    updater: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => {
    setExpandedOverride((prev) => {
      const base = prev ?? defaultExpanded;
      return typeof updater === 'function' ? updater(base) : updater;
    });
  };


  const toggleExpanded = (key: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedEvents(new Set(sortedEvents.map(getEventKey)));
  };

  const collapseAll = () => {
    setExpandedEvents(new Set());
  };

  if (sortedEvents.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-neutral-500 dark:text-slate-400">
          No events found for this file.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-neutral-200 dark:border-slate-700">
        <span className="text-sm text-neutral-600 dark:text-slate-400">
          {sortedEvents.length} event{sortedEvents.length !== 1 ? 's' : ''}
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-neutral-200 dark:bg-slate-700" />

        {/* Events */}
        <div className="space-y-4">
          {sortedEvents.map((event) => {
            const key = getEventKey(event);
            const isExpanded = expandedEvents.has(key);
            const statusClass = getEventStatusClass(event);

            return (
              <div key={key} className="relative pl-10">
                {/* Timeline dot */}
                <div
                  className={`absolute left-2 w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 ${getStatusDotColor(
                    statusClass
                  )}`}
                />

                {/* Event Card */}
                <div
                  className={`p-4 rounded-lg border transition-all ${
                    statusClass === 'error'
                      ? 'bg-danger-50 dark:bg-danger-900/10 border-danger-200 dark:border-danger-800'
                      : statusClass === 'warning'
                      ? 'bg-warning-50 dark:bg-warning-900/10 border-warning-200 dark:border-warning-800'
                      : statusClass === 'success'
                      ? 'bg-success-50 dark:bg-success-900/10 border-success-200 dark:border-success-800'
                      : 'bg-neutral-50 dark:bg-slate-800/50 border-neutral-200 dark:border-slate-700'
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getEventIcon(event.eventType)}</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {(event.eventType || 'UNKNOWN').replace('_', ' ')}
                        </span>
                      </div>
                      <div className="mt-1">
                        <StatusBadge variant={getStatusVariant(event.status)} size="sm">
                          {event.status}
                        </StatusBadge>
                      </div>
                    </div>
                    <span className="text-xs text-neutral-500 dark:text-slate-400">
                      {formatTimestamp(event.timestamp)}
                    </span>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-slate-700 space-y-2">
                      {event.taskName && (
                        <DetailRow label="Task" value={event.taskName} />
                      )}
                      {event.partnerName && (
                        <DetailRow label="Partner" value={event.partnerName} />
                      )}
                      {event.sourcePath && (
                        <DetailRow label="Source" value={event.sourcePath} />
                      )}
                      {event.destinationPath && (
                        <DetailRow label="Destination" value={event.destinationPath} />
                      )}
                      {event.fileSize !== undefined && (
                        <DetailRow label="File Size" value={formatFileSize(event.fileSize)} />
                      )}
                      {event.checksum && (
                        <DetailRow label="Checksum" value={event.checksum} mono />
                      )}
                      {event.errorMessage && (
                        <DetailRow
                          label="Error"
                          value={event.errorMessage}
                          error
                        />
                      )}

                      {/* THUB-205: exact published SNS notification. Rendered only
                          when the event actually published (null otherwise — the
                          common case until lane4b's call-site threading lands). */}
                      {event.notification && (
                        <NotificationPanel notification={event.notification} />
                      )}
                    </div>
                  )}

                  {/* Toggle Button */}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(key)}
                    className="mt-3 text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUpIcon />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDownIcon />
                        Show Details
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Detail row component for journey event details
 */
interface DetailRowProps {
  label: string;
  value: string;
  mono?: boolean;
  error?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, mono, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
    <span className="text-xs font-medium text-neutral-500 dark:text-slate-400 min-w-[80px]">
      {label}:
    </span>
    <span
      className={`text-xs break-all ${
        error
          ? 'text-danger-700 dark:text-danger-400'
          : mono
          ? 'font-mono text-neutral-600 dark:text-slate-400'
          : 'text-neutral-700 dark:text-slate-300'
      }`}
    >
      {value}
    </span>
  </div>
);

/**
 * THUB-205: expandable "Notification" panel for a single journey event.
 *
 * Shows the EXACT published SNS message (body) and messageAttributes, each with
 * copy-to-clipboard, plus the publish proof (messageId/topicArn/publishedAt).
 * Only ids and S3 locations are shown — no secrets. The `notification` block is
 * passed through verbatim (never snakeToCamel'd), so messageAttributes keeps its
 * snake_case AWS-filter keys and body.metadata keeps user-defined keys.
 */
const NotificationPanel: React.FC<{ notification: NotificationBlock }> = ({ notification }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<'body' | 'attrs' | null>(null);

  const bodyJson = React.useMemo(
    () => JSON.stringify(notification.body ?? {}, null, 2),
    [notification.body]
  );
  const attrsJson = React.useMemo(
    () => JSON.stringify(notification.messageAttributes ?? {}, null, 2),
    [notification.messageAttributes]
  );

  const handleCopy = async (which: 'body' | 'attrs', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 1500);
    } catch {
      // Clipboard can be unavailable (insecure context / permissions). Non-fatal:
      // the JSON is still visible on screen for manual copy.
    }
  };

  const attrEntries = Object.entries(notification.messageAttributes ?? {});

  return (
    <div className="mt-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/40 dark:bg-primary-900/10">
      {/* Panel header / toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-primary-700 dark:text-primary-300">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          Notification
        </span>
        <span className="text-primary-500 dark:text-primary-400">
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {/* Publish proof (ids + timestamp only, no secrets) */}
          <div className="space-y-1">
            <DetailRow label="Message ID" value={notification.messageId || '-'} mono />
            <DetailRow label="Topic ARN" value={notification.topicArn || '-'} mono />
            <DetailRow label="Published" value={formatTimestamp(notification.publishedAt)} />
            {notification.sequenceNumber && (
              <DetailRow label="Sequence" value={notification.sequenceNumber} mono />
            )}
          </div>

          {/* Message body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Message Body
              </span>
              <button
                type="button"
                onClick={() => handleCopy('body', bodyJson)}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                {copied === 'body' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-all bg-white dark:bg-slate-900 border border-neutral-200 dark:border-slate-700 rounded p-2 max-h-64 overflow-y-auto text-neutral-700 dark:text-slate-300">
              {bodyJson}
            </pre>
          </div>

          {/* Message attributes (snake_case, as consumers filter on them) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Message Attributes
              </span>
              <button
                type="button"
                onClick={() => handleCopy('attrs', attrsJson)}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                {copied === 'attrs' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            {attrEntries.length > 0 ? (
              <div className="bg-white dark:bg-slate-900 border border-neutral-200 dark:border-slate-700 rounded p-2 space-y-1">
                {attrEntries.map(([k, v]) => (
                  <div key={k} className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-2">
                    <span className="text-[11px] font-mono font-medium text-neutral-500 dark:text-slate-400 min-w-[110px]">
                      {k}
                    </span>
                    <span className="text-[11px] font-mono break-all text-neutral-700 dark:text-slate-300">
                      {String(v)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-neutral-400 dark:text-slate-500 italic">No attributes</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper functions

function getEventKey(event: { eventId?: string; id?: string; fileId?: string; timestamp: string }): string {
  return event.eventId || event.id || `${event.fileId}-${event.timestamp}`;
}

// THUB-186: was a local `new Date(x).toLocaleString(undefined, ...)` copy — it
// ignored the user's timezone AND parsed naive backend timestamps as local time.
// activeTimezone() is read per call so a zone change re-renders correctly.
function formatTimestamp(timestamp: string): string {
  return formatDateTime(timestamp, activeTimezone(), 'full');
}

function formatFileSize(bytes?: number): string {
  if (bytes === undefined || bytes === null) return '-';
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function getEventStatusClass(event: { eventType?: string; status: string }): string {
  const statusLower = event.status.toLowerCase();
  const eventType = event.eventType;

  if (
    eventType === 'FAILED' ||
    statusLower.includes('error') ||
    statusLower.includes('fail')
  ) {
    return 'error';
  }
  if (eventType === 'QUARANTINED' || statusLower === 'quarantined') {
    return 'warning';
  }
  if (
    eventType === 'DELIVERED' ||
    eventType === 'ARCHIVED' ||
    statusLower.includes('success') ||
    statusLower === 'ready' ||
    statusLower === 'delivered'
  ) {
    return 'success';
  }
  return 'info';
}

function getStatusVariant(status: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  const statusLower = status.toLowerCase();
  if (
    statusLower.includes('success') ||
    statusLower === 'ready' ||
    statusLower === 'delivered' ||
    statusLower === 'picked_up' ||
    statusLower === 'completed'
  ) {
    return 'success';
  }
  if (statusLower === 'quarantined' || statusLower.includes('warn')) {
    return 'warning';
  }
  if (statusLower.includes('fail') || statusLower.includes('error')) {
    return 'danger';
  }
  if (statusLower === 'processing' || statusLower === 'in_progress') {
    return 'info';
  }
  return 'neutral';
}

function getStatusDotColor(statusClass: string): string {
  switch (statusClass) {
    case 'error':
      return 'bg-danger-500';
    case 'warning':
      return 'bg-warning-500';
    case 'success':
      return 'bg-success-500';
    case 'info':
      return 'bg-primary-500';
    default:
      return 'bg-neutral-400';
  }
}

function getEventIcon(eventType?: string): string {
  if (!eventType) return '\u2022';
  switch (eventType) {
    case 'RECEIVED':
      return '\u2B07';
    case 'PROCESSING':
      return '\u2699';
    case 'VALIDATED':
      return '\u2714';
    case 'ARCHIVED':
      return '\uD83D\uDCC2';
    case 'QUARANTINED':
      return '\u26A0';
    case 'DELIVERED':
      return '\u2B06';
    case 'PICKED_UP':
      return '\uD83D\uDCE4';
    case 'FAILED':
      return '\u2718';
    default:
      return '\u2022';
  }
}

// Icon components
const ChevronDownIcon: React.FC = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon: React.FC = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

export default FileAuditPage;
